class MultishipAddressForm extends BaseMultishipForm {
  constructor() {
    super();
    this.handlesTaxes = true;
    this.autocomplete = null;
    this.enteredAddress = null;
    this.recommendedAddress = null;
    this.customerId = null;
    this.customerAddresses = [];
    this.defineFieldNames();
    this.focusableElements = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
  }

  initialize() {
    this.loadCustomerContext();
    super.initialize();
    window.multishipAddressSync.register(this);
  }

  loadCustomerContext() {
    this.customerId = this.getAttribute('data-customer-id');
    this.customerAddresses = this.getCustomerAddressesFromAttribute();
  }

  getCustomerAddressesFromAttribute() {
    const addressesAttribute = this.getAttribute('data-customer-addresses');
    if (!addressesAttribute) return [];

    try {
      return JSON.parse(addressesAttribute);
    } catch (error) {
      console.error("Error parsing customer addresses:", error);
      return [];
    }
  }

  defineFieldNames() {
    this.fieldNames = {
      first_name: 'First Name',
      last_name: 'Last Name',
      company: 'Company',
      address1: 'Address',
      city: 'City',
      province: 'State/Province',
      zip: 'ZIP/Postal Code',
      country_code: 'Country',
    };
  }

  async loadSavedAddresses() {
    try {
      this.loadCustomerContext();
      
      if (this.customerAddresses && this.customerAddresses.length > 0) {
        console.log(`Loaded ${this.customerAddresses.length} customer addresses`);
        if (this.customerAddresses.length >= 20) {
          await this.fetchAllAddresses();
        }
      } else {
        console.log('No customer addresses found');
      }
    } catch (error) {
      console.error("Error loading saved addresses:", error);
      this.customerAddresses = [];
    }
  }

  async fetchAllAddresses() {
    if (!this.customerId) {
      console.error("Customer ID is missing");
      return false;
    }

    try {
      const timestamp = Date.now();
      const shopDomain = window.Shopify.shop;
      const token = this.generateSecureToken(timestamp, shopDomain, this.customerId);

      const response = await fetch(`${this.apiBaseUrl}/api/customer_addresses/`, {
        method: 'POST',
        headers: {
          'X-Request-Token': token,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch addresses: ${response.status} ${response.statusText}`);
      }

      const allAddresses = await response.json();
      this.customerAddresses = allAddresses; // Replace existing addresses with all fetched addresses
      return true;
    } catch (error) {
      console.error("Error fetching addresses:", error);
      return false;
    }
  }

  generateSecureToken(timestamp, shopDomain, customerId) {
    // Combine the elements and encode them
    return btoa(`${timestamp}:${shopDomain}:${customerId}`);
  }

  determineInitialState() {
    // Load address data consistently with loadData method
    this.addressData = this.mapDataToAddress(this.data);
    this._recommendedAddress = this.data._recommendedAddress || "";
    this._addressSelection = this.data._addressSelection || this.data["_address-selection"] || "";
    
    return this.isAddressComplete() ? "filled" : "unfilled";
  }

  getDefaultState() {
    return "unfilled";
  }

  loadData() {
    super.loadData();
    
    // Map the decoded data to our address format
    this.addressData = this.mapDataToAddress(this.data);
    
    // Load additional address-specific data
    this._recommendedAddress = this.data._recommendedAddress || "";
    this._addressSelection = this.data._addressSelection || this.data["_address-selection"] || "";
  }
  
  /**
   * Map data object to address format
   */
  mapDataToAddress(data) {
    return {
      first_name: data.firstName || "",
      last_name: data.lastName || "",
      company: data.company || "",
      address1: data.address1 || "",
      address2: data.address2 || "",
      city: data.city || "",
      province: data.provinceCode || "",
      zip: data.zip || "",
      country_code: data.countryCode || "",
    };
  }

  hasPartialAddress() {
    return Object.values(this.addressData).some(value => value !== "");
  }

  isAddressComplete() {
    return this.addressData && 
      this.addressData.first_name && 
      this.addressData.last_name && 
      this.addressData.address1 && 
      this.addressData.city && 
      this.addressData.province && 
      this.addressData.zip &&
      this.addressData.country_code;
  }

  getTemplate() {
    switch (this.state) {
      case "unfilled":
        return this.getUnfilledTemplate();
      case "form":
        return this.getFormTemplate();
      case "filled":
        return this.getFilledTemplate();
      case "confirm":
        return this.getConfirmationTemplate();
      case "unverified":
        return this.getUnverifiedAddressTemplate();
      default:
        console.error("Invalid state:", this.state);
        return "";
    }
  }

  getUnfilledTemplate() {
    return `
      ${this.baseMultishipFormStyles()}
      <button type="button" class="multiship__button" id="show-form-btn-${this.lineItemId}">
        ${this.iconCirclePlus()}
        Add recipient & address (required)
      </button>
    `;
  }

  createSavedAddressesSelect() {
    if (!this.customerId || !this.customerAddresses || this.customerAddresses.length === 0) {
      return '';
    }

    // Sort addresses, placing the default address at the top
    const sortedAddresses = this.customerAddresses.sort((a, b) => {
      if (a.default) return -1;
      if (b.default) return 1;
      return 0;
    });

    let options = '<option value="">Choose a saved address</option>';
    sortedAddresses.forEach(address => {
      const isDefault = address.default ? ' (Default)' : '';
      const displayText = `${address.first_name} ${address.last_name}, ${address.address1}, ${address.city}, ${address.province_code} ${address.zip}${isDefault}`;
      options += `<option value="${address.id}">${this.escapeHtml(displayText)}</option>`;
    });

    return `
      <div class="saved-addresses floating-label form-field">
        <label for="saved-addresses-${this.lineItemId}">Saved Addresses</label>
        <select id="saved-addresses-${this.lineItemId}" name="saved-addresses">
          ${options}
        </select>
      </div>
    `;
  }

  handleSavedAddressSelection(event) {
    const selectedAddressId = event.target.value;
    if (selectedAddressId) {
      const selectedAddress = this.customerAddresses.find(address => address.id.toString() === selectedAddressId);
      if (selectedAddress) {
        this.fillAddressForm(selectedAddress);
      }
    }
  }

  fillAddressForm(address) {
    this.shadowRoot.getElementById(`first_name-${this.lineItemId}`).value = address.first_name;
    this.shadowRoot.getElementById(`last_name-${this.lineItemId}`).value = address.last_name;
    this.shadowRoot.getElementById(`company-${this.lineItemId}`).value = address.company || '';
    this.shadowRoot.getElementById(`address1-${this.lineItemId}`).value = address.address1;
    this.shadowRoot.getElementById(`address2-${this.lineItemId}`).value = address.address2 || '';
    this.shadowRoot.getElementById(`city-${this.lineItemId}`).value = address.city;
    this.shadowRoot.getElementById(`province-${this.lineItemId}`).value = address.province_code;
    this.shadowRoot.getElementById(`zip-${this.lineItemId}`).value = address.zip;
    this.shadowRoot.getElementById(`country_code-${this.lineItemId}`).value = address.country_code;
  }

  isDuplicateAddress(newAddress, existingAddresses) {
    if (!existingAddresses || existingAddresses.length === 0) return false;

    return existingAddresses.some((existingAddress) => {
      return window.multishipAddressSync.addressesMatch(existingAddress, newAddress);
    });
  }

  async saveAddressToAccount(addressData) {
    if (!this.customerId) {
      window.multishipAddressSync.markAddressPending(addressData);
      return { success: false, reason: 'not_logged_in' };
    }

    try {
      const addressBookLoaded = await this.fetchAllAddresses();
      if (!addressBookLoaded) {
        return { success: false, reason: 'address_book_unavailable' };
      }

      return await this.saveNewAddressToAccount(addressData);
    } catch (error) {
      console.error('Error saving address to customer account:', error);
      return { success: false, reason: 'error', error };
    }
  }

  async saveNewAddressToAccount(addressData) {
    try {
      if (this.isDuplicateAddress(addressData, this.customerAddresses)) {
        return { success: false, reason: 'duplicate' };
      }

      const formData = new FormData();
      formData.append('address[first_name]', addressData.first_name);
      formData.append('address[last_name]', addressData.last_name);
      formData.append('address[company]', addressData.company || '');
      formData.append('address[address1]', addressData.address1);
      formData.append('address[address2]', addressData.address2 || '');
      formData.append('address[city]', addressData.city);
      formData.append('address[province]', addressData.province);
      formData.append('address[zip]', addressData.zip);
      formData.append('address[country]', addressData.country_code);
      formData.append('address[phone]', '');
      formData.append('form_type', 'customer_address');
      formData.append('utf8', '✓');

      const response = await fetch('/account/addresses', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error(`Failed to save address: ${response.status} ${response.statusText}`);
      }

      this.customerAddresses.push(addressData);

      return { success: true };
    } catch (error) {
      console.error('Error saving address to customer account:', error);
      return { success: false, reason: 'error', error };
    }
  }

  getFormTemplate() {
    return `
      ${this.baseMultishipFormStyles()}
      <div class="multiship__section">
        <div class="multiship-form" id="address-form-${this.lineItemId}" role="form" aria-labelledby="form-heading-${this.lineItemId}">
          <div class="multiship__header">
            <h4 id="form-heading-${this.lineItemId}">Recipient & Address</h4>
          </div>
          <div class="multiship-form__inputs">
            ${this.createSavedAddressesSelect()}
            <div class="country-code floating-label form-field">
              <label for="country_code-${this.lineItemId}">Country</label>
              <select id="country_code-${this.lineItemId}" name="country_code" required aria-required="true">
                <option value="US" ${this.addressData.country_code == "US" ? 'selected' : ''}>USA</option>
              </select>
              <div data-error="country_code" role="alert" aria-live="polite"></div>
            </div>
            <div class="first-name floating-label form-field">
              <label for="first_name-${this.lineItemId}">First Name</label>
              <input type="text" id="first_name-${this.lineItemId}" name="first_name" required aria-required="true" value="${this.escapeHtml(this.addressData.first_name)}" placeholder="">
              <div data-error="first_name" role="alert" aria-live="polite"></div>
            </div>

            <div class="last-name floating-label form-field">
              <label for="last_name-${this.lineItemId}">Last Name</label>
              <input type="text" id="last_name-${this.lineItemId}" name="last_name" required aria-required="true" value="${this.escapeHtml(this.addressData.last_name)}" placeholder="">
              <div data-error="last_name" role="alert" aria-live="polite"></div>
            </div>

            <div class="address-1 floating-label form-field">
              <label for="address1-${this.lineItemId}">Address 1</label>
              <input type="text" id="address1-${this.lineItemId}" name="address1" required aria-required="true" value="${this.escapeHtml(this.addressData.address1)}" placeholder="">
              <div data-error="address1" role="alert" aria-live="polite"></div>
            </div>

            <div class="address-2 floating-label form-field">
              <label for="address2-${this.lineItemId}">Address 2</label>
              <input type="text" id="address2-${this.lineItemId}" name="address2" value="${this.escapeHtml(this.addressData.address2)}" placeholder="">
              <div data-error="address2" role="alert" aria-live="polite"></div>
            </div>

            <div class="company floating-label form-field">
              <label for="company-${this.lineItemId}">Company</label>
              <input type="text" id="company-${this.lineItemId}" name="company" value="${this.escapeHtml(this.addressData.company)}" placeholder="">
              <div data-error="company" role="alert" aria-live="polite"></div>
            </div>

            <div class="city floating-label form-field">
              <label for="city-${this.lineItemId}">City</label>
              <input type="text" id="city-${this.lineItemId}" name="city" required aria-required="true" value="${this.escapeHtml(this.addressData.city)}" placeholder="">
              <div data-error="city" role="alert" aria-live="polite"></div>
            </div>

            <div class="state-province floating-label form-field">
              <label for="province-${this.lineItemId}">State/Province</label>
              <input type="text" id="province-${this.lineItemId}" name="province" required aria-required="true" value="${this.escapeHtml(this.addressData.province)}" placeholder="">
              <div data-error="province" role="alert" aria-live="polite"></div>
            </div>

            <div class="zip floating-label form-field">
              <label for="zip-${this.lineItemId}">ZIP/Postal Code</label>
              <input type="text" id="zip-${this.lineItemId}" name="zip" required aria-required="true" value="${this.escapeHtml(this.addressData.zip)}" placeholder="">
              <div data-error="zip" role="alert" aria-live="polite"></div>
            </div>

          </div>
          <div class="multiship__actions">
            <button type="button" id="save-address-btn-${this.lineItemId}" class="button">Save <span class="sr-only">Address</span></button>
            <button type="button" id="cancel-address-btn-${this.lineItemId}" class="link-button">Cancel</button>
          </div>
        </div>
      </div>
    `;
  }

  getConfirmationTemplate() {
    return `
      ${this.baseMultishipFormStyles()}
      <div class="multiship__section">
        <div class="multiship__header">
          <h4 id="confirm-heading-${this.lineItemId}">Confirm Your Address</h4>
        </div>
        <p id="confirm-description-${this.lineItemId}">To ensure accurate delivery, we strongly suggest selecting the Recommended Address because it has been validated as a deliverable shipping address. If you choose instead to proceed with the address you entered, Lynch Creek Farm will not be responsible for undelivered or late packages.</p>
        <div class="address-options" role="radiogroup" aria-labelledby="confirm-heading-${this.lineItemId}" aria-describedby="confirm-description-${this.lineItemId}">
          <div class="address-option">
            <label class="full-address-label" for="recommended-address-${this.lineItemId}">
              <input type="radio" id="recommended-address-${this.lineItemId}" name="address-selection" value="recommended" checked>
              <strong>Recommended Address:</strong><br>
              ${this.formatAddress(this.recommendedAddress)}
            </label>
          </div>
          <div class="address-option">
            <label class="full-address-label" for="entered-address-${this.lineItemId}">
              <input type="radio" id="entered-address-${this.lineItemId}" name="address-selection" value="entered">
              <strong>You entered:</strong><br>
              ${this.formatAddress(this.enteredAddress)}
            </label>
          </div>
        </div>
        <div class="multiship__actions">
          <button type="button" id="confirm-address-btn-${this.lineItemId}" class="button">Save & Continue</button>
          <button type="button" id="go-back-btn-${this.lineItemId}" class="link-button">Go Back</button>
        </div>
      </div>
    `;
  }

  getFilledTemplate() {
    return `
      ${this.baseMultishipFormStyles()}
      <div class="multiship__section">
        <div class="multiship__header">
          <h4>Recipient & Address</h4>
          <button type="button" class="link-button" id="edit-address-btn-${this.lineItemId}">Edit</button>
        </div>
        <p>
          ${this.escapeHtml(this.addressData.first_name)} ${this.escapeHtml(this.addressData.last_name)}<br>
          ${this.addressData.company ? `${this.escapeHtml(this.addressData.company)}<br>` : ""}
          ${this.escapeHtml(this.addressData.address1)}<br>
          ${this.addressData.address2 && this.addressData.address2 !== "" ? `${this.escapeHtml(this.addressData.address2)}<br>` : ""}
          ${this.escapeHtml(this.addressData.city)}, ${this.escapeHtml(this.addressData.province)} ${this.escapeHtml(this.addressData.zip)} ${this.escapeHtml(this.addressData.country_code)}
        </p>
      </div>
    `;
  }

  getUnverifiedAddressTemplate() {
    return `
      ${this.baseMultishipFormStyles()}
      <div class="multiship__section">
        <div class="multiship__header">
          <h4>Address Verification Issue</h4>
        </div>
        <p>We're sorry, the address you entered cannot be found by our shipping validation service. Please double check the shipping address or enter another variation of the address.</p>
        <p>
          ${this.escapeHtml(this.unverifiedAddress.first_name)} ${this.escapeHtml(this.unverifiedAddress.last_name)}<br>
          ${this.unverifiedAddress.company ? `${this.escapeHtml(this.unverifiedAddress.company)}<br>` : ""}
          ${this.escapeHtml(this.unverifiedAddress.address1)}<br>
          ${this.unverifiedAddress.address2 ? `${this.escapeHtml(this.unverifiedAddress.address2)}<br>` : ''}
          ${this.escapeHtml(this.unverifiedAddress.city)}, ${this.escapeHtml(this.unverifiedAddress.province)} ${this.escapeHtml(this.unverifiedAddress.zip)}<br>
          ${this.escapeHtml(this.unverifiedAddress.country_code)}
        </p>
        <div class="multiship__actions">
          <button type="button" id="save-anyway-btn-${this.lineItemId}" class="button">Save Anyway</button>
          <button type="button" id="edit-address-btn-${this.lineItemId}" class="link-button">Edit Address</button>
        </div>
      </div>
    `;
  }

  formatAddress(address) {
    return `
      ${this.escapeHtml(address.first_name)} ${this.escapeHtml(address.last_name)}<br>
      ${address.company ? `${this.escapeHtml(address.company)}<br>` : ""}
      ${this.escapeHtml(address.address1)}<br>
      ${address.address2 ? this.escapeHtml(address.address2) + '<br>' : ''}
      ${this.escapeHtml(address.city)}, ${this.escapeHtml(address.province)} ${this.escapeHtml(address.zip)}<br>
      ${this.escapeHtml(address.country_code)}
    `;
  }

  attachEventListeners() {
    if (this.state === "unfilled") {
      const showFormBtn = this.shadowRoot.getElementById(`show-form-btn-${this.lineItemId}`);
      if (showFormBtn) {
        showFormBtn.addEventListener("click", () => this.showForm());
      }
    } else if (this.state === "form") {
      const form = this.shadowRoot.getElementById(`address-form-${this.lineItemId}`);
      if (form) {
        form.addEventListener("keydown", (event) => {
          if (event.key === "Enter") {
            this.handleSubmit();
          }
        });
      }
      const cancelBtn = this.shadowRoot.getElementById(`cancel-address-btn-${this.lineItemId}`);
      if (cancelBtn) {
        cancelBtn.addEventListener("click", () => this.cancelForm());
      }
      const saveBtn = this.shadowRoot.getElementById(`save-address-btn-${this.lineItemId}`);
      if (saveBtn) {
        saveBtn.addEventListener("click", () => this.handleSubmit());
      }
      const savedAddressesSelect = this.shadowRoot.getElementById(`saved-addresses-${this.lineItemId}`);
      if (savedAddressesSelect) {
        savedAddressesSelect.addEventListener("change", (event) => this.handleSavedAddressSelection(event));
      }
      this.initAutocomplete();
    } else if (this.state === "filled") {
      const editBtn = this.shadowRoot.getElementById(`edit-address-btn-${this.lineItemId}`);
      if (editBtn) {
        editBtn.addEventListener("click", () => this.showForm());
      }
    } else if (this.state === "confirm") {
      const radioButtons = this.shadowRoot.querySelectorAll('input[type="radio"]');
      const confirmBtn = this.shadowRoot.getElementById(`confirm-address-btn-${this.lineItemId}`);

      radioButtons.forEach((radio, index) => {
        radio.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
            event.preventDefault();
            const prevIndex = (index - 1 + radioButtons.length) % radioButtons.length;
            radioButtons[prevIndex].focus();
            radioButtons[prevIndex].checked = true;
          } else if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
            event.preventDefault();
            const nextIndex = (index + 1) % radioButtons.length;
            radioButtons[nextIndex].focus();
            radioButtons[nextIndex].checked = true;
          } else if (event.key === 'Enter') {
            event.preventDefault();
            confirmBtn.click(); // Simulate a click on the confirm button
          }
        });
      });

      const goBackBtn = this.shadowRoot.getElementById(`go-back-btn-${this.lineItemId}`);

      if (confirmBtn) {
        confirmBtn.addEventListener("click", () => this.confirmAddress());
      }
      if (goBackBtn) {
        goBackBtn.addEventListener("click", () => this.showForm());
      }
    }
    if (this.state === "unverified") {
      const saveAnywayBtn = this.shadowRoot.getElementById(`save-anyway-btn-${this.lineItemId}`);
      const editAddressBtn = this.shadowRoot.getElementById(`edit-address-btn-${this.lineItemId}`);

      if (saveAnywayBtn) {
        saveAnywayBtn.addEventListener("click", () => this.saveUnverifiedAddress());
      }
      if (editAddressBtn) {
        editAddressBtn.addEventListener("click", () => this.editUnverifiedAddress());
      }
    }

  }

  async saveUnverifiedAddress() {
    try {
      // Check if the address has actually changed
      const addressChanged = !this.addressData || 
        this.addressData.address1 !== this.unverifiedAddress.address1 ||
        this.addressData.city !== this.unverifiedAddress.city ||
        this.addressData.province !== this.unverifiedAddress.province ||
        this.addressData.zip !== this.unverifiedAddress.zip ||
        this.addressData.country_code !== this.unverifiedAddress.country_code;

      let taxRates = this.taxRates;
      let taxPerItemQuantity = this.getStoredTaxPerItemQuantity();
      
      if (addressChanged) {
        taxRates = await this.calculateTaxes(this.unverifiedAddress);
        taxPerItemQuantity = this.calculateTaxPerItemQuantity(taxRates);
      }

      const updatedData = {
        ...this.data,
        firstName: this.unverifiedAddress.first_name,
        lastName: this.unverifiedAddress.last_name,
        company: this.unverifiedAddress.company,
        address1: this.unverifiedAddress.address1,
        address2: this.unverifiedAddress.address2,
        city: this.unverifiedAddress.city,
        provinceCode: this.unverifiedAddress.province,
        zip: this.unverifiedAddress.zip,
        countryCode: this.unverifiedAddress.country_code,
        _recommendedAddress: null,
        "_address-selection": "unverified",
      };

      // Save all the data using the encoder
      this.saveAddressData(updatedData, this.unverifiedAddress, taxRates, taxPerItemQuantity);

      const result = await this.updateCart({});

      this.addressData = this.unverifiedAddress;

      if (result.error) {
        throw new Error(result.error);
      }

      await this.saveAddressToAccount(this.unverifiedAddress);

      if (this.isAddressComplete()) {
        this.state = "filled";
      } else {
        console.error("Address is incomplete after saving");
        this.state = "form";
      }

      this.render();
    } catch (error) {
      console.error("Error updating cart:", error);
      this.state = "form";
      this.render();
      this.showError('address1', 'An error occurred while updating the cart. Please try again.');
    }
  }

  editUnverifiedAddress() {
    this.state = "form";
    this.render();
  }

  async confirmAddress() {
    const selectedAddressType = this.shadowRoot.querySelector('input[name="address-selection"]:checked').value;
    const selectedAddress = selectedAddressType === "recommended" ? this.recommendedAddress : this.enteredAddress;

    try {
      // Check if the address has actually changed
      const addressChanged = !this.addressData || 
        this.addressData.address1 !== selectedAddress.address1 ||
        this.addressData.city !== selectedAddress.city ||
        this.addressData.province !== selectedAddress.province ||
        this.addressData.zip !== selectedAddress.zip ||
        this.addressData.country_code !== selectedAddress.country_code;

      let taxRates = this.taxRates;
      let taxPerItemQuantity = this.getStoredTaxPerItemQuantity();
      
      if (addressChanged) {
        taxRates = await this.calculateTaxes(selectedAddress);
        taxPerItemQuantity = this.calculateTaxPerItemQuantity(taxRates);
      }

      const updatedData = {
        ...this.data,
        firstName: selectedAddress.first_name,
        lastName: selectedAddress.last_name,
        company: selectedAddress.company,
        address1: selectedAddress.address1,
        address2: selectedAddress.address2,
        city: selectedAddress.city,
        provinceCode: selectedAddress.province,
        zip: selectedAddress.zip,
        countryCode: selectedAddress.country_code,
        _recommendedAddress: `${this.recommendedAddress.address1}, ${this.recommendedAddress.city}, ${this.recommendedAddress.province}, ${this.recommendedAddress.zip}, ${this.recommendedAddress.country_code}`,
        "_address-selection": selectedAddressType,
      };

      // Save all the data using the encoder
      this.saveAddressData(updatedData, selectedAddress, taxRates, taxPerItemQuantity);

      const result = await this.updateCart({});

      this.addressData = selectedAddress;

      if (result.error) {
        throw new Error(result.error);
      }

      await this.saveAddressToAccount(selectedAddress);

      if (this.isAddressComplete()) {
        this.state = "filled";
      } else {
        console.error("Address is incomplete after confirmation");
        this.state = "form";
      }

      this.render();
    } catch (error) {
      console.error("Error updating cart:", error);
      this.state = "form";
      this.render();
      this.showError('address1', 'An error occurred while updating the cart. Please try again.');
    }
  }

  calculateTaxPerItemQuantity(taxRates) {
    const itemPrice = this.getAttribute("data-line-price"); // Integer representing the price in cents
    const itemPriceInDollars = itemPrice / 100; // Convert cents to dollars
    const taxAmount = this.calculateTaxAmount(itemPriceInDollars, taxRates);
    return Math.round(taxAmount * 100); // Convert dollars to cents
  }

  calculateTaxAmount(price, taxRates) {
    const totalTaxRate = taxRates.reduce((sum, tax) => sum + parseFloat(tax.rate), 0);
    return parseFloat((price * totalTaxRate).toFixed(2)); // Calculate tax and round to 2 decimal places
  }

  cancelForm() {
    this.state = this.isAddressComplete() ? "filled" : "unfilled";
    this.render();
  }

  initAutocomplete() {
    const addressInput = this.shadowRoot.getElementById(`address1-${this.lineItemId}`);
    if (addressInput && typeof google !== 'undefined' && google.maps && google.maps.places) {
      this.autocomplete = new google.maps.places.Autocomplete(addressInput, {
        componentRestrictions: { country: ['us', 'ca'] }
      });
      this.autocomplete.addListener('place_changed', () => this.fillInAddress());
    } else {
      console.error('Google Maps API is not loaded or address input not found');
    }
  }

  fillInAddress() {
    const place = this.autocomplete.getPlace();
    let address1 = '';
    let city = '';
    let state = '';
    let postalCode = '';
    let countryCode = '';

    for (const component of place.address_components) {
      const componentType = component.types[0];

      switch (componentType) {
        case 'street_number':
          address1 = `${component.long_name} ${address1}`;
          break;
        case 'route':
          address1 += component.short_name;
          break;
        case 'postal_code':
          postalCode = component.long_name;
          break;
        case 'locality':
          city = component.long_name;
          break;
        case 'administrative_area_level_1':
          state = component.short_name.toUpperCase();
          break;
        case 'country':
          countryCode = component.short_name;
          break;
      }
    }

    this.shadowRoot.getElementById(`address1-${this.lineItemId}`).value = address1;
    this.shadowRoot.getElementById(`city-${this.lineItemId}`).value = city;
    this.shadowRoot.getElementById(`province-${this.lineItemId}`).value = state;
    this.shadowRoot.getElementById(`zip-${this.lineItemId}`).value = postalCode;
    this.shadowRoot.getElementById(`country_code-${this.lineItemId}`).value = countryCode;

    this.addressData = {
      ...this.addressData,
      address1,
      city,
      province: state,
      zip: postalCode,
      country_code: countryCode
    };
  }

  async handleSubmit() {
    const form = this.shadowRoot.getElementById(`address-form-${this.lineItemId}`);
    if (!form) {
      console.error("Form element not found");
      return;
    }

    const inputs = form.querySelectorAll("input, select");
    const addressData = {};

    inputs.forEach((input) => {
      let value = input.value;

      // Normalize fullwidth ampersand (U+FF06) to regular ampersand (U+0026)
      // This handles East Asian keyboard input methods
      if (value.includes('\uFF06')) {
        value = value.replace(/\uFF06/g, '&');
      }

      // Convert province/state code to uppercase
      if (input.name === 'province') {
        addressData[input.name] = value.toUpperCase();
      } else {
        addressData[input.name] = value;
      }
    });

    addressData.country_code = this.shadowRoot.getElementById(`country_code-${this.lineItemId}`).value;

    if (!this.validateForm(addressData)) {
      return;
    }

    try {
      await super.handleSubmit();

      const verificationResult = await this.verifyAddress(addressData);

      if (verificationResult.verified) {
        this.enteredAddress = addressData;
        this.recommendedAddress = verificationResult.address;
        this.showAddressConfirmation();
      } else {
        this.showUnverifiedAddressWarning(addressData);
      }

      // Clear shipping data
      this.clearShippingData();

    } catch (error) {
      console.error("Error verifying address:", error);
      this.showError('address1', 'An error occurred while verifying the address. Please try again later.');
    }
  }

  clearShippingData() {
    delete this.data.shippingMethodSku;
    delete this.data.shippingMethod;
    delete this.data.shippingPrice;
    delete this.data.requestedShipDate;
    delete this.data.approximateArrivalDate;
  }

  showUnverifiedAddressWarning(addressData) {
    this.state = "unverified";
    this.unverifiedAddress = addressData;
    this.render();
  }

  showAddressConfirmation() {
    this.state = "confirm";
    this.render();
  }

  showError(field, message) {
    const errorElement = this.shadowRoot.querySelector(`[data-error="${field}"]`);
    if (errorElement) {
      errorElement.textContent = message;
      errorElement.classList.add('error-message');
      errorElement.setAttribute('role', 'alert');
    } else {
      console.warn(`Error element for field "${field}" not found`);
    }
  }

  clearErrors() {
    const errorElements = this.shadowRoot.querySelectorAll('[data-error]');
    errorElements.forEach(element => {
      element.textContent = '';
      element.classList.remove('error-message');
    });
  }

  validateForm(data) {
    this.clearErrors();
    let isValid = true;
    const requiredFields = ["first_name", "last_name", "address1", "city", "province", "zip", "country_code"];

    const fieldLimits = {
      first_name: 35,
      last_name: 35,
      company: 35,
      address1: 35,
      address2: 35,
      city: 30,
      province: 30,
      zip: 10,
      country_code: 2
    };

    requiredFields.forEach(field => {
      if (!data[field] || data[field].trim() === "") {
        const fieldName = this.fieldNames[field] || field;
        this.showError(field, `${fieldName} is required.`);
        isValid = false;
      } else if (data[field].length > fieldLimits[field]) {
        const fieldName = this.fieldNames[field] || field;
        this.showError(field, `${fieldName} must not exceed ${fieldLimits[field]} characters.`);
        isValid = false;
      }
    });

    // Check non-required field (address2) for length
    if (data.address2 && data.address2.length > fieldLimits.address2) {
      this.showError('address2', `Address 2 must not exceed ${fieldLimits.address2} characters.`);
      isValid = false;
    }
    if (data.company && data.company.length > fieldLimits.company) {
      this.showError('company', `Company must not exceed ${fieldLimits.company} characters.`);
      isValid = false;
    }

    // Check for PO Box in address1 and address2
    if (this.isPOBox(data.address1) || this.isPOBox(data.address2)) {
      this.showError('address1', 'PO Box addresses are not accepted. Please provide a physical address.');
      isValid = false;
    }

    // Validate zip code format
    if (data.country_code === 'US' && !/^\d{5}(-\d{4})?$/.test(data.zip)) {
      this.showError('zip', 'Please enter a valid US ZIP code (e.g., 12345 or 12345-6789).');
      isValid = false;
    } else if (data.country_code === 'CA' && !/^[A-Za-z]\d[A-Za-z] \d[A-Za-z]\d$/.test(data.zip)) {
      this.showError('zip', 'Please enter a valid Canadian postal code (e.g., A1A 1A1).');
      isValid = false;
    }

    return isValid;
  }

  async verifyAddress(addressData) {
    try {
      const response = await fetch(`${this.apiBaseUrl}/api/address_verifications`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          address: {
            address1: addressData.address1,
            address2: addressData.address2,
            city: addressData.city,
            province_code: addressData.province,
            zip: addressData.zip,
            country_code: addressData.country_code,
          },
        }),
      });

      if (!response.ok) {
        throw new Error(`Address verification failed: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();

      if (result.verified) {
        return {
          verified: true,
          address: {
            first_name: addressData.first_name,
            last_name: addressData.last_name,
            company: addressData.company,
            address1: result.address.street1,
            address2: result.address.street2,
            city: result.address.city,
            province: result.address.state,
            zip: result.address.zip,
            country_code: result.address.country
          }
        };
      } else {
        return {
          verified: false,
          address: addressData
        };
      }
    } catch (error) {
      console.error("Error verifying address:", error);
      return {
        verified: false,
        address: addressData
      };
    }
  }


  async calculateTaxes(address) {
    try {
      const response = await fetch(`${this.apiBaseUrl}/api/taxes/lookup`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          shop_domain: "lynch-creek-wreaths.myshopify.com",
          items: [
            {
              sku: this.getAttribute("data-line-sku"),
              properties: {
                _data: {
                  firstName: address.first_name,
                  lastName: address.last_name,
                  address1: address.address1,
                  city: address.city,
                  countryCode: address.country_code,
                  provinceCode: address.province,
                  zip: address.zip,
                },
              },
            },
          ],
        }),
      });

      if (!response.ok) {
        throw new Error(`Tax calculation failed: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      return result[0].tax_rates;
    } catch (error) {
      console.error("Error calculating taxes:", error);
      return [];
    }
  }

  async showForm() {
    try {
      await this.loadSavedAddresses();
      this.state = "form";
      this.render();
    } catch (error) {
      console.error("Error loading saved addresses:", error);
      this.showError('general', 'An error occurred while loading saved addresses. Please try again.');
    }
  }

  showError(field, message) {
    const errorElement = this.shadowRoot.querySelector(`[data-error="${field}"]`);
    if (errorElement) {
      errorElement.textContent = message;
      errorElement.classList.add('error-message');
      errorElement.setAttribute('role', 'alert');
    } else {
      console.warn(`Error element for field "${field}" not found`);
    }
  }
  
  /**
   * Save address data to cart properties
   */
  saveAddressData(updatedData, address, taxRates, taxPerItemQuantity) {
    updatedData._taxPerItemQuantity = taxPerItemQuantity;

    // Save the main data object using the intelligent save method
    this.saveDataProperty(updatedData);
    
    // Save human-readable properties
    this.updateProperty("Recipient name", `${address.first_name} ${address.last_name}`);
    this.updateProperty("Address", this.formatAddressString(address));
    
    // Save tax information
    if (taxRates && taxRates === this.taxRates && this.taxRatesString) {
      this.updateProperty("_taxRates", this.taxRatesString);
    } else if (taxRates && typeof taxRates === 'object') {
      const encodedTaxRates = window.CartDataEncoder.encode(taxRates);
      this.updateProperty("_taxRates", encodedTaxRates);
      this.taxRatesString = encodedTaxRates;
      this.taxRatesAlreadyEncoded = false;
      this.taxRates = taxRates;
    } else if (this.taxRatesString) {
      this.updateProperty("_taxRates", this.taxRatesString);
    } else {
      this.updateProperty("_taxRates", "[]");
      this.taxRatesString = "[]";
      this.taxRatesAlreadyEncoded = false;
    }
    this.updateProperty("_taxPerItemQuantity", String(taxPerItemQuantity));
  }

  getStoredTaxPerItemQuantity() {
    const directTaxProperty = this.properties.find(
      (prop) => prop[0] === "_taxPerItemQuantity",
    );
    const directTaxValue = directTaxProperty ? parseInt(directTaxProperty[1], 10) : NaN;

    if (!Number.isNaN(directTaxValue)) {
      return directTaxValue;
    }

    const nestedTaxValue = parseInt(this.data?._taxPerItemQuantity, 10);
    if (!Number.isNaN(nestedTaxValue)) {
      return nestedTaxValue;
    }

    return 0;
  }
  
  /**
   * Format address as a readable string
   */
  formatAddressString(address) {
    return `${address.address1}, ${address.city}, ${address.province} ${address.zip}, ${address.country_code}`;
  }
}

customElements.define("multiship-address-form", MultishipAddressForm);
